import { useEffect, useMemo, useState } from "react";
import { useJson, getJson, nf, ago, card, control, sectionLabel, Chip, IdBadge, KevBadge, RULE_ENGINE, RULE_TYPE, rulePath, LEVEL_COLOR, LEVEL_LABEL } from "./shared";
import { StixBar, StixPill, StixTypeTag, queryOf } from "./Stix";
import { graphSrcHash } from "../graph/handoff";

// Detection rules: Sigma, YARA, Snort and Suricata (detections.js on the server), from
// SigmaHQ, YARA Forge, Malpedia, Talos and Emerging Threats. All rules and a list per
// engine (filters kept in the address), a page per rule, and the same table on technique,
// malware and CVE profiles. Downloads hold every rule the filters pick, as STIX or as the
// rules themselves (rules-export.js).

const PAGE = 50;
const ENGINE_ORDER = ["sigma", "yara", "snort", "suricata"];
const SOURCE_NAME = { sigmahq: "SigmaHQ", "yara-forge": "YARA Forge", malpedia: "Malpedia (analysts' rules)", talos: "Talos", "et-open": "Emerging Threats Open" };
const TIER_LABEL = { core: "Core", extended: "Extended", full: "Full only" };
const TIER_NOTE = { core: "YARA Forge's core set: the fewest false positives", extended: "In YARA Forge's extended set, not its core", full: "Only in YARA Forge's full set: the most false positives" };
const STATUS_LABEL = { stable: "Stable", test: "Test", experimental: "Experimental", deprecated: "Deprecated", unsupported: "Unsupported" };
// Filters kept in the address on the rule lists.
const KEYS = ["engine", "src", "repo", "tier", "level", "status", "q", "author", "since", "sort"];
// A source that hasn't changed its rules in this many days is flagged.
const STALE_DAYS = 90;

// "Neo23x0/signature-base" → "signature-base (Neo23x0)".
const repoName = (r) => { const [owner, name] = String(r).split("/"); return name ? `${name} (${owner})` : r; };

function keepInHash(state) {
  const [route, query = ""] = window.location.hash.split("?");
  const q = new URLSearchParams(query);
  for (const k of KEYS) { if (state[k] && !(k === "sort" && state[k] === "level")) q.set(k, state[k]); else q.delete(k); }
  const next = `${route}${q.toString() ? `?${q}` : ""}`;
  if (next !== window.location.hash) window.history.replaceState(null, "", next);
}

function LevelPill({ level, ui }) {
  const { fs } = ui;
  if (!level) return null;
  const c = LEVEL_COLOR[level] || "#71717a";
  return <span style={{ fontSize: fs(10), fontWeight: 800, color: c, background: c + "1a", border: `1px solid ${c}55`, borderRadius: 5, padding: "1px 7px", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: 0.5 }}>{LEVEL_LABEL[level] || level}</span>;
}

// Links out of a rule: ATT&CK techniques, CVEs (with KEV), malware families.
function RuleLinks({ r, ui, max = 6 }) {
  const { B, getConf } = ui;
  const links = [
    ...r.techniques.map(t => ({ key: t.attackId, href: `#/kb/e/${t.attackId}`, label: t.name ? `${t.attackId} ${t.name}` : t.attackId, color: getConf(t.type || "attack-pattern").color })),
    ...r.cves.map(c => ({ key: c.id, href: `#/kb/e/${c.id}`, label: c.id, kev: c.kev, color: c.kev ? "#dc2626" : getConf("vulnerability").color })),
    ...r.families.map(f => ({ key: f.id, href: `#/kb/e/${encodeURIComponent(f.attackId || f.id)}`, label: f.name, color: getConf(f.type || "malware").color })),
  ];
  if (!links.length) return null;
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
      {links.slice(0, max).map(l => (
        <a key={l.key} href={l.href} onClick={e => e.stopPropagation()} style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Chip ui={ui} color={l.color}>{l.label}</Chip>
          {l.kev && <KevBadge kev ui={ui} />}
        </a>
      ))}
      {links.length > max && <span style={{ fontSize: ui.fs(10), color: B.textDim }}>+{links.length - max} more</span>}
    </div>
  );
}

function RuleRow({ r, ui, showEngine, first }) {
  const { B, fs } = ui;
  return (
    <div style={{ padding: "11px 16px", borderTop: first ? "none" : `1px solid ${B.border}`, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
        <LevelPill level={r.level} ui={ui} />
        {showEngine && <Chip ui={ui} color="#0ea5e9">{RULE_ENGINE[r.engine]?.label}</Chip>}
        <a href={rulePath(r.engine, r.id)} style={{ flex: "1 1 260px", minWidth: 0, fontSize: fs(13), fontWeight: 700, color: B.text, textDecoration: "none", wordBreak: "break-word" }}>{r.title}</a>
        {r.disabled && <Chip ui={ui} title="Talos ships this rule commented out; enable it if it fits your network.">off by default</Chip>}
      </div>
      <div style={{ display: "flex", gap: "4px 12px", flexWrap: "wrap", fontSize: fs(11), color: B.textDim, alignItems: "center" }}>
        <IdBadge id={r.engine === "yara" ? r.id : r.engine === "sigma" ? r.id.slice(0, 8) : `sid ${r.id}`} ui={ui} />
        {r.source && <span>{SOURCE_NAME[r.source] || r.source}</span>}
        {r.engine === "yara" && r.group && <span>{repoName(r.group)}</span>}
        {r.tier && <span title={TIER_NOTE[r.tier]}>{TIER_LABEL[r.tier]} set</span>}
        {r.engine !== "yara" && r.category && <span>{r.category}</span>}
        {r.engine !== "yara" && r.group && <span>{r.group}</span>}
        {r.date && <span>updated {r.date}</span>}
      </div>
      <RuleLinks r={r} ui={ui} />
    </div>
  );
}

// Download every rule the filters pick: STIX, or the rules in their own format.
function RuleExportBar({ query, total, engines, ui }) {
  const { B, fs, HP } = ui;
  if (!total) return null;
  const native = engines.length === 1 && engines[0] !== "sigma" ? `.${RULE_ENGINE[engines[0]]?.ext || "txt"}` : ".zip";
  const btn = { ...HP.navBtn, padding: "7px 12px", fontSize: fs(12), textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 7, color: B.text, whiteSpace: "nowrap" };
  const what = total === 1 ? "this rule" : `all ${nf(total)} rules`;
  return (
    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      <span style={{ fontSize: fs(11), color: B.textDim }}>Download {total === 1 ? "it" : nf(total)} as</span>
      <a href={`/api/kb/rules/export${queryOf({ ...query, format: "stix", download: 1 })}`} download aria-label={`Download ${what} as STIX 2.1`} title={`Download ${what} as one STIX 2.1 bundle: each rule an indicator whose pattern is the rule, linked to the techniques, malware and CVEs it detects`} style={{ ...btn, borderColor: "#0ea5e966" }}>
        <StixPill ui={ui} style={{ padding: "1px 5px" }} />
      </a>
      <a href={`/api/kb/rules/export${queryOf({ ...query, format: "native" })}`} download aria-label={`Download ${what} as rules (${native})`} title={`Download ${what} in their own format, ready for your tools${native === ".zip" ? " (a zip: a file per engine, Sigma as .yml files)" : ""}`} style={btn}>
        rules ({native})
      </a>
      <a href={graphSrcHash(`/api/kb/stix/rules${queryOf(query)}`)} title="See the most severe 300 as an interactive graph" style={{ ...btn, gap: 0 }}>Graph</a>
    </span>
  );
}

function Select({ value, onChange, label, options, ui, all }) {
  if (!options.length && !value) return null;
  return (
    <select value={value} onChange={e => onChange(e.target.value)} aria-label={label} style={{ ...control(ui), maxWidth: "100%" }}>
      <option value="">{all}</option>
      {value && !options.some(o => o[0] === value) && <option value={value}>{value}</option>}
      {options.map(([v, l, n]) => <option key={v} value={v}>{l} ({nf(n)})</option>)}
    </select>
  );
}

const facetOptions = (counts, label = (k) => k, order) => Object.entries(counts || {})
  .sort((a, b) => (order ? order.indexOf(a[0]) - order.indexOf(b[0]) : b[1] - a[1]))
  .map(([k, n]) => [k, label(k), n]);

// A paged, filterable table of rules. params scopes it: { engine } or { entity }, plus any
// filters to start with. With keepHash, the filters are kept in the address.
export function RuleTable({ ui, params = {}, byEngine, showFilters = true, keepHash = false }) {
  const { B, fs } = ui;
  const [f, setF] = useState(() => Object.fromEntries(KEYS.map(k => [k, typeof params[k] === "string" ? params[k] : ""])));
  const [q, setQ] = useState(f.q);
  const [author, setAuthor] = useState(f.author);
  const [pages, setPages] = useState([]);
  const [state, setState] = useState({ loading: true, error: "", total: 0, facets: {} });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v, ...(k === "engine" && v !== "yara" ? { repo: "", tier: "" } : {}), ...(k === "src" ? { repo: "", tier: "" } : {}) }));

  useEffect(() => { const t = setTimeout(() => setF(prev => (prev.q === q ? prev : { ...prev, q })), 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => { const t = setTimeout(() => setF(prev => (prev.author === author ? prev : { ...prev, author })), 300); return () => clearTimeout(t); }, [author]);
  useEffect(() => { if (keepHash) keepInHash(f); }, [f, keepHash]);

  // What the server is asked: the scope (an entity, or an engine's own page) and the filters.
  const query = useMemo(() => {
    const out = {};
    if (params.entity) out.entity = params.entity;
    for (const k of KEYS) if (f[k]) out[k] = f[k];
    if (!out.sort) out.sort = "level";
    return out;
  }, [params.entity, f]);
  const base = `/api/kb/rules${queryOf(query)}`;

  useEffect(() => {
    let live = true;
    setState(s => ({ ...s, loading: true, error: "" }));
    getJson(`${base}${base.includes("?") ? "&" : "?"}offset=0&limit=${PAGE}`)
      .then(d => { if (!live) return; setPages([d.items]); setState({ loading: false, error: "", total: d.total, facets: d.facets || {}, byEngine: d.byEngine }); })
      .catch(e => live && setState(s => ({ ...s, loading: false, error: e.message })));
    return () => { live = false; };
  }, [base]);

  const items = pages.flat();
  const more = () => {
    getJson(`${base}${base.includes("?") ? "&" : "?"}offset=${items.length}&limit=${PAGE}`).then(d => setPages(p => [...p, d.items])).catch(() => {});
  };

  const fc = state.facets || {};
  const engineCounts = fc.engine && Object.keys(fc.engine).length ? fc.engine : byEngine || state.byEngine || {};
  const engines = ENGINE_ORDER.filter(e => engineCounts[e]);
  const lockedEngine = params.lockEngine ? f.engine : "";
  const showEnginePills = !lockedEngine && engines.length > 1;
  const exportEngines = f.engine ? [f.engine] : engines;
  const pill = (on) => ({ fontSize: fs(12), fontWeight: on ? 700 : 500, color: on ? B.text : B.textMuted, background: on ? B.primary + "18" : B.bgCard, border: `1px solid ${on ? B.primary + "55" : B.border}`, borderRadius: 20, padding: "5px 12px", cursor: "pointer", fontFamily: "'Satoshi', sans-serif" });
  const filtered = KEYS.some(k => k !== "sort" && k !== "engine" && f[k]) || (!lockedEngine && f.engine);
  const clear = () => { setQ(""); setAuthor(""); setF(prev => Object.fromEntries(KEYS.map(k => [k, k === "engine" && lockedEngine ? prev.engine : k === "sort" ? prev.sort : ""]))); };

  return (
    <div>
      {showEnginePills && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          <button onClick={() => set("engine", "")} style={pill(!f.engine)}>All <span style={{ color: B.textDim }}>{nf(Object.values(engineCounts).reduce((a, b) => a + b, 0))}</span></button>
          {engines.map(e => <button key={e} onClick={() => set("engine", e)} style={pill(f.engine === e)}>{RULE_ENGINE[e].label} <span style={{ color: B.textDim }}>{nf(engineCounts[e])}</span></button>)}
        </div>
      )}
      {showFilters && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by title, ID, technique (T1059) or CVE…" aria-label="Filter rules" style={{ ...control(ui), flex: "1 1 240px", minWidth: 0 }} />
          <Select ui={ui} value={f.src} onChange={v => set("src", v)} label="Source" all="Any source" options={facetOptions(fc.src, k => SOURCE_NAME[k] || k)} />
          {(f.engine === "yara" || !f.engine) && <Select ui={ui} value={f.repo} onChange={v => set("repo", v)} label="YARA repository" all="Any YARA repository" options={facetOptions(fc.repo, repoName)} />}
          {(f.engine === "yara" || !f.engine) && <Select ui={ui} value={f.tier} onChange={v => set("tier", v)} label="YARA Forge set" all="Any YARA Forge set" options={facetOptions(fc.tier, k => TIER_LABEL[k] || k, ["core", "extended", "full"])} />}
          <Select ui={ui} value={f.level} onChange={v => set("level", v)} label="Severity" all="Any severity" options={facetOptions(fc.level, k => LEVEL_LABEL[k] || "No severity", [...Object.keys(LEVEL_LABEL), "none"])} />
          {(f.engine === "sigma" || !f.engine) && <Select ui={ui} value={f.status} onChange={v => set("status", v)} label="Sigma status" all="Any Sigma status" options={facetOptions(fc.status, k => STATUS_LABEL[k] || k, Object.keys(STATUS_LABEL))} />}
          <input value={author} onChange={e => setAuthor(e.target.value)} placeholder="Author…" aria-label="Author" style={{ ...control(ui), flex: "0 1 160px", minWidth: 0 }} />
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: fs(12), color: B.textDim }}>
            Updated since
            <input type="date" value={f.since} onChange={e => set("since", e.target.value)} aria-label="Updated since" style={{ ...control(ui), padding: "5px 8px" }} />
          </label>
          <select value={f.sort || "level"} onChange={e => set("sort", e.target.value)} aria-label="Sort" style={control(ui)}>
            <option value="level">Sort: Severity</option>
            <option value="recent">Sort: Recently updated</option>
            <option value="name">Sort: Title</option>
          </select>
          {filtered && <button onClick={clear} style={{ ...control(ui), cursor: "pointer" }}>Clear filters</button>}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontSize: fs(11), color: B.textDim }}>{state.loading ? "Loading…" : `${nf(state.total)} rule${state.total === 1 ? "" : "s"}${state.total > PAGE ? " · downloads hold all of them, not just this page" : ""}`}</span>
        {!state.loading && <RuleExportBar query={query} total={state.total} engines={exportEngines} ui={ui} />}
      </div>
      {state.error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 8 }}>{state.error}</div>}
      {items.length > 0 && (
        <div style={{ ...card(ui), overflow: "hidden" }}>
          {items.map((r, i) => <RuleRow key={`${r.engine}:${r.id}`} r={r} ui={ui} showEngine={!f.engine} first={i === 0} />)}
        </div>
      )}
      {!state.loading && !items.length && !state.error && <div style={{ fontSize: fs(12), color: B.textDim }}>No rules match.</div>}
      {items.length < state.total && (
        <button onClick={more} style={{ ...control(ui), cursor: "pointer", marginTop: 12 }}>Show {Math.min(PAGE, state.total - items.length)} more</button>
      )}
    </div>
  );
}

// Where the rules come from, how many, and how fresh each source is.
function RuleSources({ sources, engine, ui }) {
  const { B, fs } = ui;
  const rows = (sources || []).filter(s => !engine || s.engine === engine);
  if (!rows.length) return null;
  const stale = (s) => s.released && (Date.now() - Date.parse(`${s.released}T00:00:00Z`)) / 86400000 > STALE_DAYS;
  const cell = { padding: "8px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  return (
    <div style={{ ...card(ui), padding: "14px 18px", marginBottom: 16, minWidth: 0 }}>
      <div style={{ ...sectionLabel(ui), marginBottom: 6 }}>Sources</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
          <thead>
            <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
              {["Source", "Engine", "Rules", "Their latest", "Read here", "Licence"].map(h => <th key={h} style={{ padding: "6px 12px", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(s => (
              <tr key={s.key}>
                <td style={cell}><a href={s.site} target="_blank" rel="noreferrer noopener" style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{s.name}</a></td>
                <td style={{ ...cell, color: B.textDim }}>{RULE_ENGINE[s.engine]?.label}</td>
                <td style={cell}><a href={`#/kb/rules?src=${encodeURIComponent(s.key)}`} style={{ color: B.accent }}>{nf(s.count)}</a></td>
                <td style={{ ...cell, color: stale(s) ? "#d97706" : B.textMuted, whiteSpace: "nowrap" }} title={stale(s) ? `Hasn't changed its rules in over ${STALE_DAYS} days` : undefined}>
                  {s.released ? `${stale(s) ? "⚠ " : ""}${s.released}` : "not given"}{s.version && !String(s.version).includes(String(s.released || "").replace(/-/g, "")) && !String(s.version).includes(s.released || "~") ? ` (${s.version})` : ""}
                </td>
                <td style={{ ...cell, color: s.error ? "#dc2626" : B.textMuted }}>{s.error ? `failed (${s.error}); keeping the last copy` : s.builtAt ? ago(s.builtAt) : "loading…"}</td>
                <td style={{ ...cell, color: B.textMuted }}>{s.license}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8, lineHeight: 1.5 }}>
        Every source is read again each day. "Their latest" is when the source last changed its rules (its release, or the date on its file).
        YARA Forge collects public repositories (Florian Roth's signature-base and Malpedia's generated rules among them), checks and scores every rule, and sorts them into a core, an extended and a full set; filter by set or repository below.
      </div>
    </div>
  );
}

export function KbRuleList({ engine, meta, ui, params = {} }) {
  const { B, fs } = ui;
  const all = engine === "rules";
  const c = RULE_ENGINE[engine];
  const info = all ? null : meta?.detections?.engines?.[engine];
  const total = all ? Object.values(meta?.detections?.engines || {}).reduce((a, e) => a + (e.count || 0), 0) : info?.count;
  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "4px 0 6px", flexWrap: "wrap" }}>
        <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: 0, color: B.text }}>{all ? "All rules" : `${c.label} rules`}</h2>
        <span style={{ fontSize: fs(13), color: "#0ea5e9", fontWeight: 800 }}>{total ? nf(total) : ""}</span>
        <StixTypeTag ui={ui} type={`indicator · pattern_type ${all ? "sigma, yara, snort, suricata" : engine}`} title={`Each rule is a STIX 2.1 indicator whose pattern is the ${all ? "" : `${c.label} `}rule itself`} />
      </div>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 12px", maxWidth: "110ch", lineHeight: 1.6 }}>
        {all ? "Every detection rule here, from five public sources: Sigma for logs, YARA for files and memory, Snort and Suricata for network traffic. Filter them by source, repository, severity, author or date, and download everything the filters pick as STIX 2.1 or as rules ready for your tools." : c.blurb}
      </p>
      {meta && !total && <div style={{ ...card(ui), padding: 16, fontSize: fs(12), color: B.textMuted, marginBottom: 12 }}>The rule sets download a few minutes after the server starts. Check back shortly.</div>}
      <RuleSources sources={meta?.detections?.sources} engine={all ? null : engine} ui={ui} />
      <RuleTable key={engine} ui={ui} params={{ ...params, ...(all ? {} : { engine, lockEngine: true }) }} keepHash />
    </>
  );
}

export function KbRule({ engine, id, ui }) {
  const { B, fs, HP, TypeIcon } = ui;
  const { data: r, error, loading } = useJson(`/api/kb/rule/${encodeURIComponent(engine)}/${encodeURIComponent(id)}`);
  const [copied, setCopied] = useState(false);
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  if (error) return <div style={{ color: "#dc2626", fontSize: fs(13) }}>{error}</div>;
  const c = RULE_ENGINE[engine];
  const copy = () => navigator.clipboard?.writeText(r.text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  const facts = [
    ["Severity", r.level ? LEVEL_LABEL[r.level] : null],
    ["Rule ID", engine === "snort" || engine === "suricata" ? `sid ${r.id}${r.revision ? ` rev ${r.revision}` : ""}` : r.id],
    ["Status", r.status],
    ["Author", r.author],
    ["Created", r.created],
    ["Modified", r.modified],
    [engine === "sigma" ? "Log source" : "Category", r.category],
    ["Source", r.source?.source],
    [engine === "yara" ? "Source repository" : engine === "sigma" ? "Rule set" : "Class", engine === "yara" && r.group ? repoName(r.group) : r.group],
    ["YARA Forge set", r.tier ? `${TIER_LABEL[r.tier]} (${TIER_NOTE[r.tier].replace(/^YARA Forge's core set: /, "")})` : null],
    ["Score", r.score != null ? `${r.score} / 100` : null],
    ["Quality", r.quality != null ? `${r.quality} / 100` : null],
    ["Licence", r.license],
  ].filter(([, v]) => v);
  const btn = { ...HP.navBtn, padding: "7px 13px", fontSize: fs(12) };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: fs(12), color: B.textDim }}><a href={`#/kb/${engine}`} style={{ color: B.textDim, textDecoration: "none" }}>{c.label} rules</a> / {r.engine === "yara" ? r.id : r.engine === "sigma" ? r.id.slice(0, 8) : `sid ${r.id}`}</div>
      <div style={{ ...card(ui), padding: "18px 22px", borderTop: `3px solid ${LEVEL_COLOR[r.level] || "#0ea5e9"}` }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#0ea5e91a", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><TypeIcon type={RULE_TYPE} color="#0ea5e9" size={20} /></div>
          <div style={{ flex: "1 1 300px", minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ ...sectionLabel(ui), color: "#0ea5e9" }}>{c.label} rule</span>
              <LevelPill level={r.level} ui={ui} />
              {r.disabled && <Chip ui={ui}>off by default</Chip>}
            </div>
            <h1 style={{ fontSize: fs(24), fontWeight: 900, margin: "4px 0 0", color: B.text, wordBreak: "break-word" }}>{r.title}</h1>
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px 22px", flexWrap: "wrap", marginTop: 14, paddingTop: 12, borderTop: `1px solid ${B.border}`, fontSize: fs(12) }}>
          {facts.map(([k, v]) => <span key={k} style={{ minWidth: 0, wordBreak: "break-word" }}><span style={{ color: B.textDim }}>{k}: </span><span style={{ color: B.text }}>{v}</span></span>)}
          {r.malpedia && <span><span style={{ color: B.textDim }}>Malpedia: </span><a href={r.malpedia.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{r.malpedia.id} ↗</a></span>}
        </div>
        <StixBar apiKey={`rule:${engine}:${id}`} ui={ui} />
      </div>

      {r.description && r.description !== r.title && (
        <div style={{ ...card(ui), padding: "16px 20px" }}>
          <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Description</div>
          <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.65, whiteSpace: "pre-wrap", maxWidth: "110ch" }}>{r.description}</div>
        </div>
      )}

      {(r.techniques.length + r.cves.length + r.families.length) > 0 && (
        <div style={{ ...card(ui), padding: "16px 20px" }}>
          <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Detects</div>
          <RuleLinks r={r} ui={ui} max={40} />
          {r.families.length > 0 && engine === "yara" && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8 }}>{r.malpedia ? "The malware family is Malpedia's, named in the rule." : "Malware families are matched from the rule's name."}</div>}
        </div>
      )}

      <div style={{ ...card(ui), overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${B.border}`, flexWrap: "wrap" }}>
          <span style={{ ...sectionLabel(ui), flex: 1 }}>Rule</span>
          <button onClick={copy} style={btn}>{copied ? "Copied ✓" : "Copy"}</button>
          <a href={`/api/kb/rules/export${queryOf({ format: "native", rule: `${engine}:${r.id}` })}`} download style={{ ...btn, textDecoration: "none", color: B.text }} title={engine === "yara" ? "With the imports and any private rules it needs, so it compiles on its own" : undefined}>Download .{engine === "sigma" ? "zip" : c.ext}</a>
        </div>
        <pre style={{ margin: 0, padding: "14px 16px", fontSize: fs(12), lineHeight: 1.55, color: B.text, background: B.bg, overflowX: "auto", whiteSpace: engine === "snort" || engine === "suricata" ? "pre-wrap" : "pre", wordBreak: engine === "snort" || engine === "suricata" ? "break-all" : "normal", maxHeight: 640 }}>{r.text}</pre>
      </div>

      {(r.references.length > 0 || r.hashes.length > 0) && (
        <div style={{ ...card(ui), padding: "16px 20px" }}>
          {r.references.length > 0 && (
            <>
              <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>References</div>
              <ul style={{ margin: "0 0 10px", paddingLeft: 18, fontSize: fs(12), lineHeight: 1.7 }}>
                {r.references.map(u => <li key={u} style={{ wordBreak: "break-all" }}><a href={u} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{u}</a></li>)}
              </ul>
            </>
          )}
          {r.hashes.length > 0 && (
            <>
              <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Sample hashes</div>
              <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 6 }}>Files the author tested the rule against. Shown as text and never linked; they're malware.</div>
              {r.hashes.map(h => <div key={h} style={{ fontFamily: "monospace", fontSize: fs(11), color: B.textMuted, wordBreak: "break-all" }}>{h}</div>)}
            </>
          )}
        </div>
      )}

      <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.6 }}>
        From <a href={r.source.site} target="_blank" rel="noreferrer" style={{ color: B.textDim }}>{r.source.source}</a> ({r.source.license}){r.source.released ? `, whose rules last changed ${r.source.released}` : ""}{r.source.builtAt ? `; read here ${ago(r.source.builtAt)}` : ""}. Test rules in your own environment before deploying them.
      </div>
    </div>
  );
}
